import { type JSX, useState } from "react";

import {
  PaymentSourceIcon,
  SubscriptionPaymentMethodModal,
} from "features/payment-methods/components";
import useSubscriptionPaymentSource from "features/payment-methods/datahooks/useSubscriptionPaymentSource";

import RestrictedComponent from "common/components/RestrictedComponent";
import { Button } from "common/components/ui/Button";
import Skeleton from "common/components/ui/Skeleton";

const CARD_BRAND_LABELS: Record<string, string> = {
  mastercard: "Master card",
  visa: "Visa",
  american_express: "American Express",
  discover: "Discover",
  diners_club: "Diners Club",
  jcb: "JCB",
};

function formatExpiryMonth(month: number) {
  return String(month).padStart(2, "0");
}

interface SubscriptionPaymentMethodProps {
  subscriptionId: string;
  isAgency: boolean;
  isLoading?: boolean;
}

export default function SubscriptionPaymentMethod({
  subscriptionId,
  isAgency,
  isLoading,
}: SubscriptionPaymentMethodProps) {
  const [isOpen, setIsOpen] = useState(false);

  const { subscriptionPaymentSource, isLoadingSubscriptionPaymentSource } =
    useSubscriptionPaymentSource(subscriptionId, isAgency);

  const isLoadingPayment = isLoading || isLoadingSubscriptionPaymentSource;

  let content: JSX.Element;
  if (isLoadingPayment) {
    content = (
      <>
        <Skeleton className="h-6 w-10 shrink-0 rounded-md" />
        <div className="flex flex-1 flex-col gap-1">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-3 w-24" />
        </div>
      </>
    );
  } else if (subscriptionPaymentSource) {
    const isCard = subscriptionPaymentSource.type === "card";

    content = (
      <>
        <PaymentSourceIcon paymentSource={subscriptionPaymentSource} />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-xs-bold text-black-950">
            {isCard
              ? `${CARD_BRAND_LABELS[subscriptionPaymentSource.card.brand] ?? subscriptionPaymentSource.card.brand} ending in ${subscriptionPaymentSource.card.last4}`
              : subscriptionPaymentSource.paypal.email}
          </span>

          {isCard && (
            <span className="text-button-xxs text-black-500">
              Expiry{" "}
              {formatExpiryMonth(subscriptionPaymentSource.card.expiry_month)}/
              {subscriptionPaymentSource.card.expiry_year}
            </span>
          )}
        </div>
      </>
    );
  } else {
    content = (
      <span className="text-sm-medium text-black-500">
        No payment method added
      </span>
    );
  }

  return (
    <>
      <div className="flex items-center gap-3">
        {content}

        <RestrictedComponent visibleForPermissions={["billing_full"]}>
          <Button
            variant="secondary-purple"
            size="sm"
            className="ml-auto shrink-0"
            onClick={() => setIsOpen(true)}
            disabled={isLoadingPayment}
          >
            Edit
          </Button>
        </RestrictedComponent>
      </div>

      <SubscriptionPaymentMethodModal
        subscriptionId={subscriptionId}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        initialPaymentSource={subscriptionPaymentSource}
        mixpanelSource="billing_page"
      />
    </>
  );
}
